import { FC, useState } from 'react'

import { useMediaQuery } from 'react-responsive'
import { CountryCode } from 'libphonenumber-js'

import { PrimarySkeleton } from 'src/Components/Skeletons/PrimarySkeleton'
import { AdVisualPerformanceSection } from 'src/Components/AdVisualPerformanceSection'
import { FullScreenModal } from 'src/Components/FullScreenModal'
import { StickyColumnTable } from 'src/Components/StickyColumnTable'
import { TableColumn } from 'src/Components/StickyColumnTable/types'
import { CountryFlag } from 'src/Components/CountryFlag'
import {
  CountryPerformanceItem,
  useGetCountryPerformanceByIdQuery,
} from 'src/Redux/adsCampaignApi'
import { numberFormatCommas, numberToMoney } from 'src/Constants/functions'
import { MAX_TABLET_WIDTH } from 'src/Styled/variables'

import { getCTR } from '../../helpers'

import {
  CountryCell,
  Flag,
  CountryName,
  ModalContent,
  ModalTitle,
} from './styles'

interface CountryPerformanceSectionProps {
  campaignId: string
}

const countryDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' })

const getCountryName = (code: string): string => {
  try {
    return countryDisplayNames.of(code) ?? code
  } catch {
    return code
  }
}

const columns: TableColumn<CountryPerformanceItem>[] = [
  {
    key: 'country',
    title: 'Country',
    align: 'left',
    fixed: 'left',
    width: 120,
    render: (row) => (
      <CountryCell>
        <Flag>
          <CountryFlag
            country={row.country as CountryCode}
            width={24}
            height={15}
          />
        </Flag>
        <CountryName>{getCountryName(row.country)}</CountryName>
      </CountryCell>
    ),
  },
  {
    key: 'totalActualSpend',
    title: 'Spent',
    width: 120,
    render: (row) => `$${numberToMoney(row.totalActualSpend)}`,
  },
  {
    key: 'totalImpressions',
    title: 'Impressions',
    width: 120,
    render: (row) => numberFormatCommas(row.totalImpressions),
  },
  {
    key: 'totalClicks',
    title: 'Clicks',
    width: 120,
    render: (row) => numberFormatCommas(row.totalClicks),
  },
  {
    key: 'ctr',
    title: 'CTR',
    width: 120,
    render: (row) => getCTR(row.totalClicks, row.totalImpressions),
  },
  {
    key: 'totalConversions',
    title: 'Conversions',
    width: 130,
    render: (row) => numberFormatCommas(row.totalConversions),
  },
  {
    key: 'costPerConversion',
    title: 'Cost per conversion',
    width: 160,
    render: (row) =>
      `$${numberToMoney(row.totalConversions ? row.totalActualSpend / row.totalConversions : 0)}`,
  },
]

export const CountryPerformanceSection: FC<CountryPerformanceSectionProps> = ({
  campaignId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isMobileOrTablet = useMediaQuery({ maxWidth: MAX_TABLET_WIDTH })

  const { data, isLoading, isError } = useGetCountryPerformanceByIdQuery(
    campaignId,
    { skip: !campaignId },
  )

  const rows = data ?? []

  if (isLoading) {
    return <PrimarySkeleton style={{ height: '160px', borderRadius: '16px' }} />
  }

  if (
    isError ||
    rows.length === 0 ||
    rows.some((row) => row.country === 'ALL')
  ) {
    return null
  }

  return (
    <>
      <AdVisualPerformanceSection
        title='Country performance'
        {...(isMobileOrTablet && { onExpand: () => setIsModalOpen(true) })}
      >
        <StickyColumnTable
          columns={columns}
          data={rows}
          rowKey={(row) => row.country}
        />
      </AdVisualPerformanceSection>
      <FullScreenModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        showCloseButton
      >
        <ModalContent>
          <ModalTitle>Country performance</ModalTitle>
          <StickyColumnTable
            columns={columns}
            data={rows}
            rowKey={(row) => row.country}
          />
        </ModalContent>
      </FullScreenModal>
    </>
  )
}
